import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, User, EnvelopeSimple, Phone, MapPin, ShoppingBag, Receipt,
  ChartLineUp, CalendarBlank, CurrencyEur, FloppyDisk, CircleNotch, Tag, Trophy,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = {
  completed: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700',
  shipped: 'bg-blue-100 text-blue-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  processing: 'bg-amber-100 text-amber-700',
  pending: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-red-100 text-red-700',
};
const STATUS_FR = {
  completed: 'Terminée', paid: 'Payée', shipped: 'Expédiée',
  delivered: 'Livrée', processing: 'En cours', pending: 'En attente',
  cancelled: 'Annulée', refunded: 'Remboursée', failed: 'Échouée',
  'on-hold': 'En attente',
};

const EMPTY_ADDRESS = {
  first_name: '', last_name: '', company: '', address_1: '', address_2: '',
  city: '', postcode: '', country: 'FR', phone: '',
};

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '',
    billing_address: { ...EMPTY_ADDRESS },
    shipping_address: { ...EMPTY_ADDRESS },
  });
  const [copyShipping, setCopyShipping] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/customers/${customerId}`, { withCredentials: true });
      setData(d);
      // Normalize address fields (both WC API "_1/postcode" and CSV "address/postal_code")
      const normAddr = (a) => ({
        ...EMPTY_ADDRESS,
        first_name: a?.first_name || '',
        last_name: a?.last_name || '',
        company: a?.company || '',
        address_1: a?.address_1 || a?.address || '',
        address_2: a?.address_2 || '',
        city: a?.city || '',
        postcode: a?.postcode || a?.postal_code || '',
        country: a?.country || 'FR',
        phone: a?.phone || '',
      });
      setForm({
        name: d.name || '',
        phone: d.phone || '',
        billing_address: normAddr(d.billing_address),
        shipping_address: normAddr(d.shipping_address),
      });
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchDetail(); }, [customerId]);

  const updateAddress = (which, field, value) => {
    setForm(prev => {
      const next = { ...prev, [which]: { ...prev[which], [field]: value } };
      if (which === 'billing_address' && copyShipping) {
        next.shipping_address = { ...next.billing_address };
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, phone: form.phone,
        billing_address: form.billing_address,
        shipping_address: copyShipping ? form.billing_address : form.shipping_address,
      };
      await axios.put(`${API_URL}/api/admin/customers/${customerId}`, payload, { withCredentials: true });
      toast.success('Fiche client mise à jour');
      fetchDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div className="p-12 text-center"><CircleNotch size={32} className="animate-spin mx-auto text-slate-400" /></div>;
  }

  const fmtMoney = (n) => `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';

  return (
    <div className="p-6 space-y-5" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/customers')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 text-white flex items-center justify-center font-bold text-lg">
            {(data.name || data.email || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="customer-name">{data.name || data.email}</h1>
            <div className="flex flex-wrap gap-2 mt-0.5 items-center">
              <span className="text-sm text-slate-500" data-testid="customer-email">{data.email}</span>
              {data.group?.name && (
                <Badge style={{ backgroundColor: '#0052FF', color: 'white' }} data-testid="customer-group">
                  {data.group.name} {data.group.discount_percent ? `(-${data.group.discount_percent}%)` : ''}
                </Badge>
              )}
              {data.is_guest_imported && (
                <span className="inline-flex items-center gap-1">
                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">Invité</Badge>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Retirer le statut "Invité" de ce client ?')) return;
                      try {
                        await axios.put(`${API_URL}/api/admin/customers/${customerId}`, { is_guest_imported: false }, { withCredentials: true });
                        toast.success('Statut Invité retiré');
                        fetchDetail();
                      } catch { toast.error('Erreur'); }
                    }}
                    className="text-[10px] text-amber-600 hover:text-amber-800 underline"
                    title="Retirer le statut Invité"
                    data-testid="remove-guest-badge-btn"
                  >
                    retirer
                  </button>
                </span>
              )}
              {data.wp_user_id && (
                <Badge variant="outline" className="text-slate-500 text-[10px]">WP #{data.wp_user_id}</Badge>
              )}
            </div>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2 bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-customer">
          {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
          Enregistrer
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={ShoppingBag} label="Commandes payées" value={data.stats.total_orders} iconColor="#10b981" testId="stat-orders" />
        <StatCard icon={CurrencyEur} label="CA généré" value={fmtMoney(data.stats.total_revenue)} iconColor="#0052FF" testId="stat-revenue" />
        <StatCard icon={ChartLineUp} label="Panier moyen" value={fmtMoney(data.stats.avg_basket)} iconColor="#c2185b" testId="stat-avg-basket" />
        <StatCard
          icon={CalendarBlank}
          label="Fréquence"
          value={data.stats.avg_frequency_days != null ? `${data.stats.avg_frequency_days} j` : '—'}
          sub={data.stats.last_order ? `Dernière: ${fmtDate(data.stats.last_order)}` : null}
          iconColor="#f59e0b"
          testId="stat-frequency"
        />
      </div>

      {/* Contact & Addresses */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Contact info */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><User size={18} className="text-slate-500" /> Coordonnées</h3>
            <div>
              <Label className="text-xs text-slate-500">Nom complet</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="form-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input value={data.email} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="06 12 34 56 78" data-testid="form-phone" />
            </div>
            <div className="pt-2 text-[12px] text-slate-500">
              Inscrit le {fmtDate(data.created_at)}
            </div>
          </CardContent>
        </Card>

        {/* Billing address */}
        <AddressEditor
          title="Adresse de facturation"
          icon={Receipt}
          addr={form.billing_address}
          onChange={(field, v) => updateAddress('billing_address', field, v)}
          testPrefix="billing"
        />

        {/* Shipping address */}
        <div>
          <AddressEditor
            title="Adresse de livraison"
            icon={MapPin}
            addr={copyShipping ? form.billing_address : form.shipping_address}
            onChange={(field, v) => updateAddress('shipping_address', field, v)}
            disabled={copyShipping}
            testPrefix="shipping"
          />
          <label className="flex items-center gap-2 mt-2 text-[12px] text-slate-600 cursor-pointer pl-1">
            <input type="checkbox" checked={copyShipping} onChange={(e) => setCopyShipping(e.target.checked)} data-testid="copy-billing-shipping" />
            Utiliser la même adresse que la facturation
          </label>
        </div>
      </div>

      {/* Order history */}
      {data.top_products?.length > 0 && (
        <Card data-testid="top-products-card">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Trophy size={18} className="text-amber-500" weight="fill" /> Produits préférés
                <span className="text-[12px] font-normal text-slate-500">Top 5 produits commandés</span>
              </h3>
            </div>
            <div className="divide-y divide-slate-100">
              {data.top_products.map((p, idx) => (
                <div key={idx} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`top-product-${idx}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#d97706' : '#cbd5e1' }}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{p.name}</p>
                    <p className="text-[12px] text-slate-500">
                      {p.qty} unité(s) · {p.order_count} commande(s){p.sku ? ` · SKU ${p.sku}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">{fmtMoney(p.revenue)}</p>
                    <p className="text-[11px] text-slate-400">total dépensé</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Order history */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <ShoppingBag size={18} className="text-slate-500" /> Historique des commandes
              <Badge variant="outline" className="text-[10px]">{data.orders.length}</Badge>
            </h3>
          </div>
          {data.orders.length === 0 ? (
            <p className="p-8 text-center text-slate-400 text-sm">Aucune commande</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">N° commande</th>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Articles</th>
                    <th className="text-left px-4 py-2">Statut</th>
                    <th className="text-left px-4 py-2">Paiement</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.orders.map(o => (
                    <tr key={o.id} className="hover:bg-slate-50" data-testid={`order-${o.id}`}>
                      <td className="px-4 py-2.5">
                        <Link to={`/admin/orders/${o.id}`} className="font-medium text-[#0052FF] hover:underline" data-testid={`order-link-${o.id}`}>
                          {o.order_number}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{o.items_count}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>
                          {STATUS_FR[o.status] || o.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 text-[12px]">{o.payment_method || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{fmtMoney(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, iconColor, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-3">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${iconColor}15` }}>
          <Icon size={18} style={{ color: iconColor }} weight="bold" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 truncate">{label}</p>
          <p className="text-[17px] font-bold text-slate-900 leading-tight">{value}</p>
          {sub && <p className="text-[10px] text-slate-400 truncate">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const AddressEditor = ({ title, icon: Icon, addr, onChange, disabled, testPrefix }) => (
  <Card className={disabled ? 'opacity-50' : ''}>
    <CardContent className="p-4 space-y-2">
      <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
        <Icon size={18} className="text-slate-500" /> {title}
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-slate-500">Prénom</Label>
          <Input value={addr.first_name || ''} onChange={(e) => onChange('first_name', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-first-name`} />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Nom</Label>
          <Input value={addr.last_name || ''} onChange={(e) => onChange('last_name', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-last-name`} />
        </div>
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Société (optionnel)</Label>
        <Input value={addr.company || ''} onChange={(e) => onChange('company', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-company`} />
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Adresse</Label>
        <Input value={addr.address_1 || ''} onChange={(e) => onChange('address_1', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-address-1`} />
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Complément</Label>
        <Input value={addr.address_2 || ''} onChange={(e) => onChange('address_2', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-address-2`} />
      </div>
      <div className="grid grid-cols-[100px_1fr] gap-2">
        <div>
          <Label className="text-[11px] text-slate-500">CP</Label>
          <Input value={addr.postcode || ''} onChange={(e) => onChange('postcode', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-postcode`} />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Ville</Label>
          <Input value={addr.city || ''} onChange={(e) => onChange('city', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-city`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-slate-500">Pays</Label>
          <Input value={addr.country || 'FR'} onChange={(e) => onChange('country', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-country`} />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Téléphone</Label>
          <Input value={addr.phone || ''} onChange={(e) => onChange('phone', e.target.value)} disabled={disabled} data-testid={`${testPrefix}-phone`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default CustomerDetailPage;
